// Client state + sync engine.
// The whole workspace is held in memory as normalized tables. Every write goes
// through `mutate(ops)`: applied optimistically, queued, POSTed to the server and
// fanned out to other clients over the websocket.

import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import type { Collections, CollectionName } from '@shared/types';
import { COLLECTIONS } from '@shared/types';
import type { BootstrapResponse, Op, ServerMessage } from '@shared/sync';
import { newId } from '@shared/ids';

export type Tables = { [K in CollectionName]: Record<string, Collections[K]> };

export interface PresenceEntry {
  userId: string;
  clientId: string;
  location?: string;
}

interface SyncState {
  db: Tables;
  /** signedOut: no valid session — the app shows the sign-in screens */
  status: 'loading' | 'ready' | 'error' | 'signedOut';
  connection: 'online' | 'offline' | 'connecting';
  pending: number;
  version: number;
  meId: string | null;
  presence: PresenceEntry[];
  /** who's typing where: `<channelId>|<parentId or ''>` → userId → when the signal lapses */
  typing: Record<string, Record<string, number>>;
  error?: string;
}

function emptyTables(): Tables {
  const t = {} as Record<string, Record<string, unknown>>;
  for (const c of COLLECTIONS) t[c] = {};
  return t as Tables;
}

export const useStore = create<SyncState>(() => ({
  db: emptyTables(),
  status: 'loading',
  connection: 'connecting',
  pending: 0,
  version: 0,
  meId: null,
  presence: [],
  typing: {},
}));

export const clientId = newId();

/** Apply ops immutably so subscribed components re-render only for touched tables. */
export function applyOps(db: Tables, ops: Op[]): Tables {
  const next = { ...db } as unknown as Record<string, Record<string, Record<string, unknown>>>;
  const copied = new Set<string>();
  for (const op of ops) {
    if (!copied.has(op.c)) {
      next[op.c] = { ...(next[op.c] ?? {}) };
      copied.add(op.c);
    }
    const col = next[op.c];
    if (op.op === 'put') col[op.id] = { ...op.data, id: op.id };
    else if (op.op === 'patch') {
      const prev = col[op.id];
      if (prev) col[op.id] = { ...prev, ...op.data, id: op.id };
    } else delete col[op.id];
  }
  return next as unknown as Tables;
}

// ---------------------------------------------------------------------------
// Mutation queue

interface Pending {
  mutationId: string;
  ops: Op[];
}
const queue: Pending[] = [];
let flushing = false;
let retryDelay = 500;

export function mutate(ops: Op[]) {
  if (ops.length === 0) return;
  useStore.setState((s) => ({ db: applyOps(s.db, ops), pending: s.pending + 1 }));
  queue.push({ mutationId: newId(), ops });
  void flush();
}

async function flush() {
  if (flushing) return;
  flushing = true;
  try {
    while (queue.length) {
      const item = queue[0];
      try {
        const res = await fetch('/api/mutate', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ clientId, mutationId: item.mutationId, ops: item.ops }),
        });
        if (res.status === 401) {
          // session expired: keep the queue, ask the user to sign in again
          useStore.setState({ status: 'signedOut' });
          return;
        }
        if (res.status >= 400 && res.status < 500) {
          // rejected: drop and resync from the server's truth
          console.warn('[atrium] mutation rejected', await res.text());
          onRejected?.(res.status);
          queue.shift();
          useStore.setState((s) => ({ pending: Math.max(0, s.pending - 1) }));
          await bootstrap();
          continue;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { version } = (await res.json()) as { version: number };
        queue.shift();
        retryDelay = 500;
        useStore.setState((s) => ({ pending: Math.max(0, s.pending - 1), version: Math.max(s.version, version) }));
      } catch {
        useStore.setState({ connection: 'offline' });
        await new Promise((r) => setTimeout(r, retryDelay));
        retryDelay = Math.min(retryDelay * 2, 10_000);
      }
    }
  } finally {
    flushing = false;
  }
}

// ---------------------------------------------------------------------------
// Bootstrap + websocket

/** Hook for surfacing rejected writes (e.g. a toast); set by the UI layer. */
export let onRejected: ((status: number) => void) | null = null;
export function setOnRejected(fn: (status: number) => void) {
  onRejected = fn;
}

export async function bootstrap() {
  try {
    const res = await fetch('/api/bootstrap', { credentials: 'same-origin' });
    if (res.status === 401) {
      useStore.setState({ status: 'signedOut', meId: null });
      return;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as BootstrapResponse;
    let db = emptyTables() as unknown as Record<string, Record<string, unknown>>;
    for (const [c, rows] of Object.entries(data.collections)) {
      if (!db[c]) continue;
      const map: Record<string, unknown> = {};
      for (const r of rows ?? []) map[(r as { id: string }).id] = r;
      db[c] = map;
    }
    // re-apply still-unacknowledged local ops on top of server truth
    let tables = db as unknown as Tables;
    for (const p of queue) tables = applyOps(tables, p.ops);
    useStore.setState({ db: tables, status: 'ready', version: data.version, meId: data.userId });
  } catch (e) {
    if (useStore.getState().status === 'loading') useStore.setState({ status: 'error', error: (e as Error).message });
    throw e;
  }
}

let socket: WebSocket | null = null;
let reconnectDelay = 500;

export function connect() {
  if (useStore.getState().status === 'signedOut') return;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const ws = new WebSocket(`${proto}://${location.host}/ws?clientId=${clientId}`);
  socket = ws;
  let opened = false;
  useStore.setState({ connection: 'connecting' });
  ws.onopen = () => {
    opened = true;
    reconnectDelay = 500;
    useStore.setState({ connection: 'online' });
    sendPresence();
    void flush();
  };
  ws.onmessage = (ev) => {
    const msg = JSON.parse(String(ev.data)) as ServerMessage;
    if (msg.type === 'hello') {
      if (msg.version !== useStore.getState().version) void bootstrap().catch(() => {});
    } else if (msg.type === 'ops') {
      // the server only sends ops this user may see, so versions can skip; a lost
      // connection is caught by the 'hello' comparison on reconnect instead
      const s = useStore.getState();
      if (msg.clientId !== clientId) useStore.setState({ db: applyOps(s.db, msg.ops), version: Math.max(s.version, msg.version) });
      // a sent message ends its author's "typing…"
      for (const op of msg.ops)
        if (op.c === 'messages' && op.op === 'put') setTyping(`${String(op.data.channelId)}|${String(op.data.parentId ?? '')}`, String(op.data.authorId), false);
      else useStore.setState({ version: Math.max(s.version, msg.version) });
    } else if (msg.type === 'resync') {
      void bootstrap().catch(() => {});
    } else if (msg.type === 'presence') {
      useStore.setState({ presence: msg.users });
    } else if (msg.type === 'typing') {
      setTyping(`${msg.channelId}|${msg.parentId ?? ''}`, msg.userId, msg.typing);
    }
  };
  ws.onclose = () => {
    if (socket !== ws) return;
    useStore.setState({ connection: 'offline' });
    // a refused upgrade may mean the session ended: bootstrap() detects that (401)
    if (!opened) void bootstrap().catch(() => {});
    setTimeout(connect, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, 10_000);
  };
}

// ---------------------------------------------------------------------------
// Typing indicator: senders repeat "typing" every few seconds, so a signal that isn't renewed lapses

const TYPING_TTL = 6000;

function setTyping(key: string, userId: string, on: boolean) {
  const cur = useStore.getState().typing[key] ?? {};
  if (!on && !(userId in cur)) return;
  const next = { ...cur };
  if (on) next[userId] = Date.now() + TYPING_TTL;
  else delete next[userId];
  useStore.setState((s) => ({ typing: { ...s.typing, [key]: next } }));
  if (!on) return;
  setTimeout(() => {
    const at = useStore.getState().typing[key]?.[userId];
    if (at !== undefined && at <= Date.now()) setTyping(key, userId, false);
  }, TYPING_TTL + 50);
}

/** Tell the others in a conversation (or one of its threads) that I'm typing, or that I stopped. */
export function sendTyping(channelId: string, parentId: string | null, typing: boolean) {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'typing', channelId, parentId, typing }));
}

let lastLocation: string | undefined;
export function sendPresence(location?: string) {
  if (location !== undefined) lastLocation = location;
  const me = useStore.getState().meId;
  if (socket?.readyState === WebSocket.OPEN && me) {
    socket.send(JSON.stringify({ type: 'presence', userId: me, location: lastLocation }));
  }
}

/** End the session on the server and return to the sign-in screen. */
export async function signOut() {
  try {
    await fetch('/api/auth/logout', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
  } finally {
    socket?.close();
    socket = null;
    window.location.assign('/login');
  }
}

// ---------------------------------------------------------------------------
// Convenience accessors

export function getDb() {
  return useStore.getState().db;
}

export function getMe(): string {
  return useStore.getState().meId ?? '';
}

export function useDb<T>(selector: (db: Tables) => T, equalityFn?: (a: T, b: T) => boolean): T {
  return useStore((s) => selector(s.db), equalityFn as (a: T, b: T) => boolean);
}

/** Like useDb but compares arrays/objects shallowly, for derived collections. */
export function useDbShallow<T>(selector: (db: Tables) => T): T {
  return useStore((s) => selector(s.db), shallow);
}

export function useMe(): string {
  return useStore((s) => s.meId ?? '');
}

export function useTable<K extends CollectionName>(c: K): Record<string, Collections[K]> {
  return useStore((s) => s.db[c]);
}

export function useEntity<K extends CollectionName>(c: K, id: string | null | undefined): Collections[K] | undefined {
  return useStore((s) => (id ? s.db[c][id] : undefined));
}

/** Small op builders */
export const put = <K extends CollectionName>(c: K, data: Collections[K]): Op => ({
  op: 'put',
  c,
  id: data.id,
  data: data as unknown as Record<string, unknown>,
});
export const patch = <K extends CollectionName>(c: K, id: string, data: Partial<Collections[K]>): Op => ({
  op: 'patch',
  c,
  id,
  data: { ...(data as Record<string, unknown>), updatedAt: Date.now() },
});
export const del = (c: CollectionName, id: string): Op => ({ op: 'del', c, id });
